import { getCoreRowModel, useReactTable } from "@tanstack/react-table";
import { useTranslate } from "@tolgee/react";
import { useMemo } from "react";
import { VirtualizedGridTable } from "#/components/ui/virtualized-grid-table";
import { mergePerpIntoSpotUsdc } from "#/features/trade/components/positions/hooks/use-effective-balances";
import {
	useHyperAccount,
	useHyperSpotBalances,
} from "#/hooks/use-hyperliquid-account";
import { useUnifiedBalance } from "#/hooks/use-unified-balance";
import {
	createPortfolioBalanceColumns,
	type PortfolioBalanceRow,
} from "./balances-columns";

const HIDDEN_COINS = new Set(["USDE", "USDT0", "USDH"]);

const GRID_CLASS = "grid grid-cols-[1.2fr_1fr_1fr] gap-3";

const coreRowModel = getCoreRowModel();

export function BalancesTable() {
	const { t } = useTranslate();
	const spot = useHyperSpotBalances();
	const account = useHyperAccount();
	const { isUnified } = useUnifiedBalance();
	const rawBalances = spot.balances;
	const accountState = account.accountState;
	const columns = useMemo(() => createPortfolioBalanceColumns(t), [t]);
	const isError = spot.isError || account.isError;
	const onRetry = () => {
		spot.refetch();
		account.refetch();
	};

	const balances: PortfolioBalanceRow[] = useMemo(() => {
		const merged = mergePerpIntoSpotUsdc({
			spotBalances: rawBalances,
			accountState,
			isUnified,
		});
		return merged.filter((b) => !HIDDEN_COINS.has(b.coin.toUpperCase()));
	}, [rawBalances, isUnified, accountState]);

	const table = useReactTable<PortfolioBalanceRow>({
		data: balances,
		columns,
		getCoreRowModel: coreRowModel,
		enableSorting: false,
	});

	return (
		<div className="min-w-0">
			<h2 className="mb-4 text-body2-bold text-foreground">
				{t("portfolio.balances")}
			</h2>
			{/* Bounded height reserves space so the loading/empty→rows transition
			    doesn't shift the tables below it (portfolio CLS). */}
			<div className="flex min-h-48 max-h-80 flex-col">
				<VirtualizedGridTable
					table={table}
					gridClass={GRID_CLASS}
					estimateSize={36}
					emptyMessage={t("portfolio.no-balances")}
					isError={isError}
					onRetry={onRetry}
				/>
			</div>
		</div>
	);
}
